/* ==========================================================================
   MAISON SOLIÈRE
   MAISON SEARCH
   ==========================================================================

   Search experience owned by Maison Solière.

   Responsibilities:
   - Open / close search
   - Focus management
   - Keyboard interaction
   - Debounced search
   - Shopify Predictive Search JSON
   - Maison-owned result rendering
   - Loading / empty states
   - Result selection
   - Escape handling

   Dawn predictive-search.js is NOT used here.
   ========================================================================== */

(() => {
  "use strict";

  /* ==========================================================================
     CONFIGURATION
     ========================================================================== */

  const CONFIG = {
    debounceDelay: 220,
    /* 10 = máximo que permite Shopify Predictive Search (2026-10-06).
       Para todo lo demás está "Ver todos los resultados". */
    productsLimit: 10,
    queriesLimit: 5,
    brandsLimit: 8,
    minChars: 1,
    sectionId: "maison-search-suggest",
    requestTimeout: 5000,
    /* 2026-10-20 — mezcla de resultados: perfumes primero, sets al final.
       Si la predictiva trae menos perfumes que esto (porque los sets la
       acaparan, ej. "ariana"), se completa con la búsqueda completa. */
    perfumesMax: 8,
    setsMax: 3,
    minPerfumesBeforeBackfill: 6,
    minStrictMatches: 3,
    setType: "Set de regalo",
  };

  /* Marcas: datos en snippets/maison-search.liquid ([data-ms-brands]).
     Alias = cómo la gente las escribe en el buscador. */
  const BRAND_ALIASES = {
    "yves-saint-laurent": ["ysl", "saint laurent"],
    "calvin-klein": ["ck"],
    "dolce-gabbana": ["d&g", "dg", "dolce"],
    "jean-paul-gaultier": ["jpg", "gaultier"],
    "carolina-herrera": ["ch"],
    "donna-karan": ["dkny"],
    "christian-dior": ["dior"],
    "giorgio-armani": ["armani"],
    "hugo-boss": ["boss"],
    "rabanne": ["paco rabanne"],
    "salvatore-ferragamo": ["ferragamo"],
    "tommy-hilfiger": ["tommy"],
    "ralph-lauren": ["polo"],
    "michael-kors": ["mk"],
    "viktor-rolf": ["v&r", "viktor and rolf"],
    "acqua-di-parma": ["adp"],
  };

  let brands = [];

  /* ==========================================================================
     STATE
     ========================================================================== */

  const state = {
    query: "",
    isOpen: false,
    isLoading: false,
    requestId: 0,
    debounceTimer: null,
    controller: null,
    previousFocus: null,
  };

  /* ==========================================================================
     ELEMENTS
     ========================================================================== */

  let search;
  let panel;
  let input;
  let form;
  let results;
  let content;
  let loading;
  let empty;
  let status;
  let closeButtons;

  /* ==========================================================================
     INITIALIZATION
     ========================================================================== */

  function init() {
    search = document.querySelector("[data-maison-search]");

    if (!search) {
      return;
    }

    brands = loadBrands();

    panel = search.querySelector("[data-maison-search-panel]");
    input = search.querySelector("[data-maison-search-input]");
    form = search.querySelector("[data-maison-search-form]");
    results = search.querySelector("[data-maison-search-results]");
    content = search.querySelector("[data-maison-search-content]");
    loading = search.querySelector("[data-maison-search-loading]");
    empty = search.querySelector("[data-maison-search-empty]");
    status = search.querySelector("[data-maison-search-status]");
    closeButtons = search.querySelectorAll("[data-maison-search-close]");

    bindEvents();
    bindTriggers();

    /*
      Allow other Maison components to control Search.

      Example:

      window.MaisonSearch.open();
      window.MaisonSearch.close();
    */
    window.MaisonSearch = {
      open,
      close,
      toggle,
      search: performSearch,
    };
  }

  /* ==========================================================================
     EVENTS
     ========================================================================== */

  function bindEvents() {
    if (input) {
      input.addEventListener("input", handleInput);
      input.addEventListener("keydown", handleInputKeydown);
      input.addEventListener("focus", handleInputFocus);
    }

    if (form) {
      form.addEventListener("submit", handleSubmit);
    }

    closeButtons.forEach((button) => {
      button.addEventListener("click", close);
    });

    document.addEventListener("keydown", handleDocumentKeydown);

    document.addEventListener("click", handleResultClick);
  }

  /* ==========================================================================
     SEARCH TRIGGERS
     ========================================================================== */

  function bindTriggers() {
    const triggers = document.querySelectorAll("[data-maison-search-open]");

    triggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        open();
      });
    });
  }

  /* ==========================================================================
     OPEN
     ========================================================================== */

  function open() {
    if (!search) {
      return;
    }

    state.previousFocus = document.activeElement;
    state.isOpen = true;

    search.setAttribute("aria-hidden", "false");
    search.classList.add("is-open");

    document.documentElement.classList.add("maison-search-open");
    document.body.classList.add("maison-search-open");

    requestAnimationFrame(() => {
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  /* ==========================================================================
     CLOSE
     ========================================================================== */

  function close() {
    if (!search) {
      return;
    }

    state.isOpen = false;

    search.setAttribute("aria-hidden", "true");
    search.classList.remove("is-open");

    document.documentElement.classList.remove("maison-search-open");
    document.body.classList.remove("maison-search-open");

    cancelPendingRequest();

    if (input) {
      input.setAttribute("aria-expanded", "false");
    }

    if (
      state.previousFocus &&
      typeof state.previousFocus.focus === "function"
    ) {
      requestAnimationFrame(() => {
        state.previousFocus.focus();
      });
    }
  }

  /* ==========================================================================
     TOGGLE
     ========================================================================== */

  function toggle() {
    if (state.isOpen) {
      close();
    } else {
      open();
    }
  }

  /* ==========================================================================
     INPUT
     ========================================================================== */

  function handleInput(event) {
    const value = event.target.value.trim();

    state.query = value;

    if (value.length === 0) {
      cancelPendingRequest();
      clearResults();
      return;
    }

    if (value.length < CONFIG.minChars) {
      cancelPendingRequest();
      clearResults();
      return;
    }

    clearTimeout(state.debounceTimer);

    state.debounceTimer = setTimeout(() => {
      performSearch(value);
    }, CONFIG.debounceDelay);
  }

  /* ==========================================================================
     INPUT FOCUS
     ========================================================================== */

  function handleInputFocus() {
    if (!state.isOpen) {
      open();
    }
  }

  /* ==========================================================================
     INPUT KEYBOARD
     ========================================================================== */

  function handleInputKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }

    /*
      Arrow navigation will be expanded later once we build
      the final Maison result architecture.
    */
  }

  /* ==========================================================================
     DOCUMENT KEYBOARD
     ========================================================================== */

  function handleDocumentKeydown(event) {
    if (!state.isOpen) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  }

  /* ==========================================================================
     FORM SUBMIT
     ========================================================================== */

  function handleSubmit(event) {
    /*
      If the user presses Enter, we allow Shopify's normal
      /search?q=... navigation.

      This is intentional.

      Predictive results are for quick selection.
      Enter takes the user to the complete search page.
    */

    if (!input || input.value.trim() === "") {
      event.preventDefault();
    }
  }

  /* ==========================================================================
     SEARCH
     ========================================================================== */

  async function performSearch(query) {
    const normalizedQuery = String(query || "").trim();

    if (!normalizedQuery || normalizedQuery.length < CONFIG.minChars) {
      clearResults();
      return;
    }

    cancelPendingRequest();

    const currentRequestId = ++state.requestId;

    state.isLoading = true;

    showLoading();
    setExpanded(true);

    state.controller = new AbortController();

    const timeout = setTimeout(() => {
      state.controller.abort();
    }, CONFIG.requestTimeout);

    try {
      const data = await fetchSuggestions(normalizedQuery, state.controller.signal);

      await arrangeProducts(data, normalizedQuery, state.controller.signal);

      /*
        Ignore old responses.

        Example:
        User types "libre"
        then immediately types "libre intense"

        If the first request finishes after the second,
        we don't want it replacing the newer results.
      */
      if (currentRequestId !== state.requestId) {
        return;
      }

      renderResults(data, normalizedQuery);
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }

      console.error("[Maison Search] Search request failed:", error);

      renderError();
    } finally {
      clearTimeout(timeout);

      if (currentRequestId === state.requestId) {
        state.isLoading = false;
        hideLoading();
      }
    }
  }

  /* ==========================================================================
     BUILD SHOPIFY SEARCH URL
     ========================================================================== */

  function shopRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : "/";
  }

  /*
    2026-10-06 — Section Rendering (sections/maison-search-suggest.liquid):
    mismos resultados que suggest.json + precio y contenido del set, que
    solo Liquid conoce. Si algo falla, se usa suggest.json como antes.
  */
  async function fetchSuggestions(query, signal) {
    try {
      const response = await fetch(buildSearchUrl(query, true), {
        method: "GET",
        headers: { Accept: "text/html" },
        signal,
      });

      if (!response.ok) {
        throw new Error(`Section request failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const node = doc.querySelector("[data-ms-suggest]");

      if (!node) {
        throw new Error("Section without data");
      }

      const parsed = JSON.parse(node.textContent);

      return { resources: { results: parsed } };
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }

      const response = await fetch(buildSearchUrl(query, false), {
        method: "GET",
        headers: { Accept: "application/json" },
        signal,
      });

      if (!response.ok) {
        throw new Error(`Search request failed: ${response.status}`);
      }

      return response.json();
    }
  }

  function buildSearchUrl(query, asSection) {
    const root = shopRoot();

    const url = new URL(
      `${root}search/suggest${asSection ? "" : ".json"}`,
      window.location.origin,
    );

    if (asSection) {
      url.searchParams.set("section_id", CONFIG.sectionId);
    }

    url.searchParams.set("q", query);

    url.searchParams.set("resources[type]", "product,query");

    url.searchParams.set("resources[limit]", String(CONFIG.productsLimit));

    /* 2026-10-20 — "last", NO "hide": con venta bajo pedido el inventario
       está en 0 con "seguir vendiendo", y la búsqueda predictiva trataba
       TODO el catálogo como no disponible → 0 perfumes en cualquier
       búsqueda (verificado en vivo: "212", "acqua", "ysl" → 0 con hide,
       10 con last). Con "last" los realmente agotados quedan al final. */
    url.searchParams.set("resources[options][unavailable_products]", "last");

    return url.toString();
  }

  /* Búsqueda completa (no predictiva), solo para completar perfumes. */
  function buildFullSearchUrl(query) {
    const url = new URL(`${shopRoot()}search`, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("type", "product");
    url.searchParams.set("options[prefix]", "last");
    url.searchParams.set("section_id", CONFIG.sectionId);
    return url.toString();
  }

  async function fetchFullSearchProducts(query, signal) {
    try {
      const response = await fetch(buildFullSearchUrl(query), {
        method: "GET",
        headers: { Accept: "text/html" },
        signal,
      });
      if (!response.ok) return [];
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const node = doc.querySelector("[data-ms-suggest]");
      if (!node) return [];
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed.products) ? parsed.products : [];
    } catch (error) {
      if (error.name === "AbortError") throw error;
      return []; // la búsqueda completa es un extra: si falla, se queda la predictiva
    }
  }

  /* ==========================================================================
     MEZCLA DE RESULTADOS (2026-10-20)
     Perfumes primero (orden de relevancia de Shopify), sets al final y
     con tope. Si los sets dejaron pocos perfumes en la predictiva, se
     completan desde la búsqueda completa — filtrando su ruido: solo
     entran productos cuyo nombre o marca tenga, para CADA palabra
     escrita, una palabra que empiece igual ("acqua di gio" → ACQUA DI
     GIO…, no "Lattafa Afaq").
     ========================================================================== */

  function productType(product) {
    return product.type || product.product_type || "";
  }

  function isSet(product) {
    return productType(product) === CONFIG.setType || /^set\b/i.test(product.title || "");
  }

  function productKey(product) {
    return product.handle || product.url || product.title;
  }

  function matchesQueryWords(product, query) {
    const words = normalize(query).split(" ").filter(Boolean);
    if (!words.length) return false;
    const haystack = normalize(`${product.title || ""} ${product.vendor || ""} ${vendorAliases(product.vendor)}`)
      .split(/[\s&·'’-]+/)
      .filter(Boolean);
    return words.every((word) => haystack.some((h) => h.startsWith(word)));
  }

  // Alias de la marca del producto (BRAND_ALIASES), para que "ch good girl"
  // o "ysl libre" cuenten como coincidencia real.
  function vendorAliases(vendor) {
    const v = normalize(vendor);
    if (!v || !brands.length) return "";
    const brand = brands.find((b) => normalize(b.name) === v);
    return brand && BRAND_ALIASES[brand.slug] ? BRAND_ALIASES[brand.slug].join(" ") : "";
  }

  async function arrangeProducts(data, query, signal) {
    const results = data && data.resources && data.resources.results;
    if (!results || !Array.isArray(results.products)) return;

    let perfumes = results.products.filter((p) => !isSet(p));
    let sets = results.products.filter(isSet);

    if (perfumes.length < CONFIG.minPerfumesBeforeBackfill) {
      const extra = await fetchFullSearchProducts(query, signal);
      const seen = new Set(results.products.map(productKey));
      extra.forEach((p) => {
        if (seen.has(productKey(p)) || !matchesQueryWords(p, query)) return;
        seen.add(productKey(p));
        if (isSet(p)) sets.push(p);
        else perfumes.push(p);
      });
    }

    // Ruido de la predictiva (ej. "libre" → Fahrenheit, Burberry Hero, que
    // coinciden por descripción): si ya hay suficientes coincidencias reales
    // por nombre/marca, se quitan las que no coinciden. Con pocas, se
    // conservan todas para no perder la tolerancia a errores de escritura
    // de Shopify ("acua" → Acqua).
    const strictPerfumes = perfumes.filter((p) => matchesQueryWords(p, query));
    if (strictPerfumes.length >= CONFIG.minStrictMatches) perfumes = strictPerfumes;
    const strictSets = sets.filter((p) => matchesQueryWords(p, query));
    if (strictSets.length >= 1 && strictPerfumes.length >= CONFIG.minStrictMatches) sets = strictSets;

    perfumes = perfumes.slice(0, CONFIG.perfumesMax);
    // Con 8 perfumes caben 2 sets (total 10); con menos perfumes, hasta 3.
    const setsRoom = perfumes.length >= CONFIG.perfumesMax ? CONFIG.productsLimit - CONFIG.perfumesMax : CONFIG.setsMax;
    sets = sets.slice(0, Math.min(CONFIG.setsMax, setsRoom));

    results.products = perfumes.concat(sets);
  }

  /* ==========================================================================
     RENDER RESULTS
     ========================================================================== */

  function renderResults(data, query) {
    if (!content) {
      return;
    }

    const matchedBrands = matchBrands(query || state.query || "");

    const resources =
      data && data.resources && data.resources.results
        ? data.resources.results
        : {};

    const products = Array.isArray(resources.products)
      ? resources.products
      : [];

    const queries = Array.isArray(resources.queries) ? resources.queries : [];

    content.innerHTML = "";

    hideEmpty();

    /*
      Nothing found.
    */

    if (products.length === 0 && queries.length === 0 && matchedBrands.length === 0) {
      content.classList.remove("has-main", "has-aside");
      showEmpty();
      updateStatus("No se encontraron resultados.");
      return;
    }

    /*
      Build our own Maison result structure.
      2026-10-06: Fragancias a la izquierda; Marcas y Sugerencias a la
      derecha. En móvil todo se apila (marcas primero, son compactas).
    */

    const fragment = document.createDocumentFragment();
    const hasAside = queries.length > 0 || matchedBrands.length > 0;

    if (hasAside) {
      const aside = document.createElement("div");
      aside.className = "ms-search__aside";

      if (matchedBrands.length > 0) {
        aside.appendChild(renderBrands(matchedBrands));
      }

      if (queries.length > 0) {
        aside.appendChild(renderSuggestions(queries));
      }

      fragment.appendChild(aside);
    }

    if (products.length > 0) {
      const main = document.createElement("div");
      main.className = "ms-search__main";
      main.appendChild(renderProducts(products, query || state.query || ""));
      fragment.appendChild(main);
    }

    content.classList.toggle("has-main", products.length > 0);
    content.classList.toggle("has-aside", hasAside);

    content.appendChild(fragment);

    updateStatus(
      `${products.length} ${
        products.length === 1 ? "resultado" : "resultados"
      } encontrados.`,
    );

    animateResults();
  }

  /* ==========================================================================
     BRANDS (2026-10-06)
     ========================================================================== */

  function loadBrands() {
    const node = document.querySelector("[data-ms-brands]");

    if (!node) {
      return [];
    }

    try {
      const list = JSON.parse(node.textContent);

      return Array.isArray(list) ? list : [];
    } catch (error) {
      console.error("[Maison Search] Brand data invalid:", error);
      return [];
    }
  }

  function normalize(text) {
    return String(text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }

  function brandScore(brand, query) {
    const candidates = [brand.name].concat(BRAND_ALIASES[brand.slug] || []);
    let best = Infinity;

    candidates.forEach((candidate) => {
      const name = normalize(candidate);

      if (!name) {
        return;
      }

      let score = Infinity;

      if (name === query) score = 0;
      else if (name.startsWith(query)) score = 1;
      else if (query.startsWith(`${name} `)) score = 1;
      else if (name.split(/[\s&-]+/).some((word) => word && word.startsWith(query))) score = 2;
      else if (query.length >= 3 && name.includes(query)) score = 3;

      best = Math.min(best, score);
    });

    return best;
  }

  function matchBrands(rawQuery) {
    const query = normalize(rawQuery);

    if (!query || brands.length === 0) {
      return [];
    }

    return brands
      .map((brand) => ({ brand, score: brandScore(brand, query) }))
      .filter((entry) => entry.score !== Infinity)
      .sort((a, b) => a.score - b.score || a.brand.name.localeCompare(b.brand.name, "es"))
      .slice(0, CONFIG.brandsLimit)
      .map((entry) => entry.brand);
  }

  function renderBrands(list) {
    const section = document.createElement("section");

    section.className = "ms-search__section ms-search__brands";

    section.innerHTML = `
      <div class="ms-search__section-heading">
        <span class="ms-search__section-title">
          Marcas
        </span>
      </div>

      <div class="ms-search__brands-list" role="list"></div>
    `;

    const container = section.querySelector(".ms-search__brands-list");

    list.forEach((brand, index) => {
      const link = document.createElement("a");

      link.className = "ms-search__brand";
      link.href = brand.url || "#";
      link.setAttribute("role", "listitem");
      link.setAttribute("aria-label", `Ver catálogo de ${brand.name}`);
      link.dataset.maisonSearchResult = "brand";
      link.style.setProperty("--ms-search-index", index);

      link.innerHTML = `
        <img
          class="ms-search__brand-logo"
          src="${escapeAttribute(brand.logo || "")}"
          alt="${escapeAttribute(brand.name || "")}"
          loading="lazy"
        >
      `;

      container.appendChild(link);
    });

    return section;
  }

  /* ==========================================================================
     SUGGESTIONS
     ========================================================================== */

  function renderSuggestions(queries) {
    const section = document.createElement("section");

    section.className = "ms-search__section ms-search__suggestions";

    section.innerHTML = `
      <div class="ms-search__section-heading">
        <span class="ms-search__section-title">
          Sugerencias
        </span>
      </div>

      <div
        class="ms-search__suggestions-list"
        role="listbox"
      ></div>
    `;

    const list = section.querySelector(".ms-search__suggestions-list");

    queries.slice(0, CONFIG.queriesLimit).forEach((query, index) => {
      const item = document.createElement("button");

      item.type = "button";

      item.className = "ms-search__suggestion";

      item.dataset.searchQuery = query.text || query.query || "";

      item.setAttribute("role", "option");

      item.style.setProperty("--ms-search-index", index);

      item.innerHTML = `
          <span class="ms-search__suggestion-text">
            ${escapeHTML(query.text || query.query || "")}
          </span>
        `;

      list.appendChild(item);
    });

    return section;
  }

  /* ==========================================================================
     PRODUCTS
     ========================================================================== */

  function renderProducts(products, query) {
    const section = document.createElement("section");

    section.className = "ms-search__section ms-search__products";

    section.innerHTML = `
      <div class="ms-search__section-heading">
        <span class="ms-search__section-title">
          Fragancias
        </span>
      </div>

      <div
        class="ms-search__products-list"
        role="list"
      ></div>
    `;

    const list = section.querySelector(".ms-search__products-list");

    products.forEach((product, index) => {
      const item = createProductItem(product, index);

      list.appendChild(item);
    });

    if (query) {
      const all = document.createElement("a");
      const searchUrl = new URL(`${shopRoot()}search`, window.location.origin);

      searchUrl.searchParams.set("q", query);
      searchUrl.searchParams.set("options[prefix]", "last");

      all.className = "ms-search__all";
      all.href = searchUrl.toString();
      all.innerHTML = `Ver todos los resultados para “${escapeHTML(query)}”`;

      section.appendChild(all);
    }

    return section;
  }

  /* ==========================================================================
     PRODUCT ITEM
     ========================================================================== */

  function createProductItem(product, index) {
    const link = document.createElement("a");

    link.className = "ms-search__product";

    link.href = product.url || "#";

    link.dataset.maisonSearchResult = "product";

    link.style.setProperty("--ms-search-index", index);

    const image = product.image || product.featured_image || null;

    const imageUrl = image && typeof image === "object" ? image.url : image;

    const title = product.title || "";

    const vendor = product.vendor || "";

    const contenido = product.contenido || "";

    const price = product.price && typeof product.price === "string" && /[^0-9.]/.test(product.price)
      ? `${product.price_varies ? "Desde " : ""}${product.price}`
      : "";

    link.innerHTML = `
      <div class="ms-search__product-media">
        ${
          imageUrl
            ? `
              <img
                class="ms-search__product-image"
                src="${escapeAttribute(imageUrl)}"
                alt="${escapeAttribute(title)}"
                loading="lazy"
                width="240"
                height="240"
              >
            `
            : `
              <div class="ms-search__product-placeholder"></div>
            `
        }
      </div>

      <div class="ms-search__product-info">

        ${
          vendor
            ? `
              <span class="ms-search__product-vendor">
                ${escapeHTML(vendor)}
              </span>
            `
            : ""
        }

        <span class="ms-search__product-title">
          ${escapeHTML(title)}
        </span>

        ${
          contenido
            ? `<span class="ms-search__product-contenido">${escapeHTML(contenido)}</span>`
            : ""
        }

        ${
          price
            ? `<span class="ms-search__product-price">${escapeHTML(price)}</span>`
            : ""
        }

      </div>
    `;

    return link;
  }

  /* ==========================================================================
     RESULT CLICK
     ========================================================================== */

  function handleResultClick(event) {
    const suggestion = event.target.closest("[data-search-query]");

    if (!suggestion) {
      return;
    }

    const query = suggestion.dataset.searchQuery;

    if (!query || !input) {
      return;
    }

    event.preventDefault();

    input.value = query;

    state.query = query;

    performSearch(query);

    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  /* ==========================================================================
     LOADING
     ========================================================================== */

  function showLoading() {
    if (!loading) {
      return;
    }

    loading.removeAttribute("aria-hidden");
    loading.classList.add("is-visible");

    if (content) {
      content.classList.add("is-loading");
    }
  }

  function hideLoading() {
    if (!loading) {
      return;
    }

    loading.setAttribute("aria-hidden", "true");
    loading.classList.remove("is-visible");

    if (content) {
      content.classList.remove("is-loading");
    }
  }

  /* ==========================================================================
     EMPTY
     ========================================================================== */

  function showEmpty() {
    if (!empty) {
      return;
    }

    empty.hidden = false;
  }

  function hideEmpty() {
    if (!empty) {
      return;
    }

    empty.hidden = true;
  }

  /* ==========================================================================
     CLEAR
     ========================================================================== */

  function clearResults() {
    if (content) {
      content.innerHTML = "";
    }

    hideEmpty();
    hideLoading();

    setExpanded(false);

    updateStatus("");
  }

  /* ==========================================================================
     ERROR
     ========================================================================== */

  function renderError() {
    if (!content) {
      return;
    }

    content.innerHTML = `
      <div class="ms-search__error">
        <p class="ms-search__error-text">
          No fue posible realizar la búsqueda.
        </p>
      </div>
    `;

    updateStatus("No fue posible realizar la búsqueda.");
  }

  /* ==========================================================================
     ARIA
     ========================================================================== */

  function setExpanded(expanded) {
    if (!input) {
      return;
    }

    input.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  function updateStatus(message) {
    if (!status) {
      return;
    }

    status.textContent = message;
  }

  /* ==========================================================================
     ANIMATION HOOK
     ========================================================================== */

  function animateResults() {
    /*
      We intentionally do not animate through JavaScript.

      JS only assigns the index.

      CSS will later control:
      - opacity
      - translate
      - stagger
      - image reveal
      - product entrance

      This keeps the animation system aligned with Maison UI.
    */

    const animatedElements = search.querySelectorAll(
      ".ms-search__suggestion, .ms-search__product",
    );

    animatedElements.forEach((element, index) => {
      element.style.setProperty("--ms-search-index", index);
    });
  }

  /* ==========================================================================
     CANCEL REQUEST
     ========================================================================== */

  function cancelPendingRequest() {
    clearTimeout(state.debounceTimer);

    state.debounceTimer = null;

    if (state.controller) {
      state.controller.abort();
      state.controller = null;
    }

    state.requestId++;
  }

  /* ==========================================================================
     ESCAPE HTML
     ========================================================================== */

  function escapeHTML(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /* ==========================================================================
     ESCAPE ATTRIBUTE
     ========================================================================== */

  function escapeAttribute(value) {
    return escapeHTML(value);
  }

  /* ==========================================================================
     START
     ========================================================================== */

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
